import { useState } from 'react';
import type { PaigeDestinationsBridge, PaigeStorageEntry } from './paige.global';

/** One step down the picker: the entry opened and the path people read so far. */
export interface Step {
  id?: string;
  name: string;
  selectable: boolean;
}

const top: Step = { name: 'Top', selectable: false };

/** The picker's walk, as StorageLocationPicker drives it. */
export type Browse = ReturnType<typeof useBrowse>;

/**
 * The picker's walk: the trail of opened places, what the last one holds, and Paige's reason when a
 * call fails. `create` makes a folder in the open place (New folder) and opens it, so "Use" is next.
 */
export function useBrowse(bridge: PaigeDestinationsBridge, destinationId: string) {
  const [trail, setTrail] = useState<Step[]>([top]);
  const [entries, setEntries] = useState<PaigeStorageEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = async (parent: string | undefined, query?: string) => {
    setError(null);
    try {
      setEntries(await bridge.locations(destinationId, parent, query));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Paige could not list the places');
    }
  };
  const into = (entry: PaigeStorageEntry) => {
    setTrail((t) => [...t, { id: entry.id, name: entry.name, selectable: entry.selectable }]);
    return load(entry.id);
  };
  return {
    trail,
    entries,
    error,
    open: () => {
      setTrail([top]);
      return load(undefined);
    },
    close: () => setEntries(null),
    search: (query: string) => load(undefined, query),
    into,
    back: (index: number) => {
      setTrail(trail.slice(0, index + 1));
      return load(trail[index]?.id);
    },
    /** Rejects with Paige's reason, for the New folder line to show. */
    create: async (name: string) => {
      const parent = trail.at(-1)?.id;
      if (!parent) throw new Error('Open the place the folder goes in first');
      await into(await bridge.createFolder(destinationId, parent, name));
    },
  };
}
